import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLenis } from "lenis/react";
import { Menu, X } from "lucide-react";

const links = [
  { label: "Opportunity", href: "#opportunity" },
  { label: "Products", href: "#products" },
  { label: "Levels", href: "#levels" },
  { label: "Territories", href: "#territories" },
  { label: "Developer", href: "#developer" },
];

export const Nav = ({ onApply }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const lenis = useLenis();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    if (href === "#top") {
      if (lenis) lenis.scrollTo(0, { duration: 1.2 });
      else window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const target = document.querySelector(href);
    if (!target) return;
    if (lenis) lenis.scrollTo(target, { offset: -80, duration: 1.2 });
    else target.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      data-testid="main-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-black/50 backdrop-blur-2xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <a href="#top" data-testid="nav-logo" onClick={(e) => { e.preventDefault(); go("#top"); }} className="flex items-center gap-3 group">
          <img src="/logo.png" alt="OED Glampire Network" className="h-11 w-11 object-contain" />
          <span className="hidden sm:block text-[11px] uppercase tracking-[0.3em] text-white/60 group-hover:text-oed-gold transition-colors">OED Glampire Network</span>
        </a>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.href.replace("#", "")}`}
              onClick={() => go(l.href)}
              className="text-sm text-white/60 hover:text-white transition-colors tracking-wide"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <button
            data-testid="nav-apply-button"
            onClick={onApply}
            className="hidden sm:inline-flex items-center bg-white text-black px-6 py-2.5 text-sm font-medium tracking-wide hover:bg-oed-gold transition-colors"
          >
            Apply Now
          </button>
          <button data-testid="nav-menu-toggle" className="md:hidden text-white" onClick={() => setOpen(!open)}>
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-black/90 backdrop-blur-2xl border-b border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <button key={l.href} data-testid={`nav-link-mobile-${l.href.replace("#", "")}`} onClick={() => go(l.href)} className="text-left text-white/70 hover:text-white text-lg">
                  {l.label}
                </button>
              ))}
              <button data-testid="nav-apply-button-mobile" onClick={() => { setOpen(false); onApply(); }} className="bg-white text-black px-6 py-3 text-sm font-medium">
                Apply Now
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
